import { useState, useRef, useEffect } from 'react';
import type { ModuleId } from '../types';

interface Props {
  activeModule: ModuleId | null;
  onAINavigate?: (targetModule: ModuleId) => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export default function FloatingAI({ activeModule, onAINavigate }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages]);

  function handleFinalResponse(text: string) {
    const navMatch = text.match(/navigate to (dashboard|vault|control|investor|ai)/i);
    if (navMatch && onAINavigate) {
      const targetModule = navMatch[1].toLowerCase() as ModuleId;
      setTimeout(() => {
        setIsOpen(false);
        onAINavigate(targetModule);
      }, 1500);
    }
  }

  async function handleSubmit() {
    const message = input.trim();
    if (!message || isLoading) return;

    setIsLoading(true);
    setInput("");
    setMessages(prev => [...prev, { role: 'user', content: message }, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(`/api/ai/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message,
          module: activeModule || "home",
          session_id: sessionId
        })
      });

      if (res.status === 401 || res.status === 403) {
        const lockMsg = res.status === 401
          ? 'Please log in to use Capital AI.'
          : 'Capital AI requires an active package. Visit the checkout page to unlock it.';
        setMessages(prev => {
          const next = [...prev];
          next[next.length - 1] = { role: 'assistant', content: lockMsg };
          return next;
        });
        return;
      }

      if (!res.ok || !res.body) {
        throw new Error(`AI request failed: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'session' && event.session_id) {
              setSessionId(event.session_id);
            } else if (event.type === 'delta') {
              fullText += event.content;
              setMessages(prev => {
                const next = [...prev];
                next[next.length - 1] = { role: 'assistant', content: fullText };
                return next;
              });
            } else if (event.type === 'error') {
              fullText = event.message;
              setMessages(prev => {
                const next = [...prev];
                next[next.length - 1] = { role: 'assistant', content: event.message };
                return next;
              });
            } else if (event.type === 'done') {
              handleFinalResponse(fullText);
            }
          } catch {
            // skip malformed SSE chunk
          }
        }
      }
    } catch {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: 'Error connecting to AI service. Please try again.' };
        return next;
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleNewConversation() {
    setSessionId(null);
    setMessages([]);
  }

  const handleNavigateToModule = (module: ModuleId) => {
    if (onAINavigate) {
      setIsOpen(false);
      onAINavigate(module);
    }
  };

  return (
    <>
      <button 
        className="floating-ai-btn"
        onClick={() => setIsOpen(true)}
        data-testid="floating-ai-btn"
      >
        <span className="floating-ai-icon">◈</span>
        <span className="floating-ai-label">Ask AI</span>
      </button>

      {isOpen && (
        <div className="ai-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="ai-modal" onClick={e => e.stopPropagation()}>
            <div className="ai-modal-header">
              <div className="ai-modal-title">
                <img src="/brand/nwg-logo-small.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." className="ai-brand-logo" />
                <span>Capital AI</span>
              </div>
              <div className="ai-modal-context">
                Context: {activeModule ? activeModule.toUpperCase() : 'HOME'}
              </div>
              <button
                className="ai-modal-new"
                onClick={handleNewConversation}
                title="New conversation"
                data-testid="ai-new-conversation"
              >
                ⟳
              </button>
              <button 
                className="ai-modal-close" 
                onClick={() => setIsOpen(false)}
                data-testid="close-ai-modal"
              >
                ✕
              </button>
            </div>

            <div className="ai-modal-body" ref={bodyRef}>
              {messages.length > 0 && (
                <div className="ai-messages-list" data-testid="ai-messages-list">
                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      className={`ai-message ai-message-${msg.role}`}
                      data-testid={`ai-message-${msg.role}`}
                    >
                      <div className="ai-message-role">
                        {msg.role === 'user' ? 'You' : 'Capital AI'}
                      </div>
                      <pre>{msg.content || (isLoading && i === messages.length - 1 ? '▋' : '')}</pre>
                    </div>
                  ))}
                </div>
              )}

              <div className="ai-modal-input-row">
                <input
                  type="text"
                  className="ai-modal-input"
                  placeholder="Ask Capital AI..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  data-testid="ai-input"
                />
                <button 
                  className="ai-modal-submit"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  data-testid="ai-submit"
                >
                  {isLoading ? '...' : '→'}
                </button>
              </div>

              <div className="ai-quick-actions">
                <button onClick={() => { setInput("analyze market conditions"); }}>Analyze</button>
                <button onClick={() => { setInput("what should I do right now"); }}>Guide Me</button>
                <button onClick={() => { setInput("explain the current state"); }}>Explain</button>
              </div>

              {onAINavigate && (
                <div className="ai-nav-actions">
                  <div className="ai-nav-label">AI Navigation Demo:</div>
                  <button onClick={() => handleNavigateToModule('dashboard')}>→ Dashboard</button>
                  <button onClick={() => handleNavigateToModule('vault')}>→ Vault</button>
                  <button onClick={() => handleNavigateToModule('investor')}>→ Investor</button>
                </div>
              )}
            </div>

            <div className="ai-modal-footer">
              Advisory only • Not financial advice • User decides
            </div>
          </div>
        </div>
      )}
    </>
  );
}
